import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Snackbar, Typography } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import { GridLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import {
  buscarSumarizacaoEcfApuracao,
  exportarSumarizacaoApuracao,
} from "../../../services/apuracao.service";
import Pagination from "../../../components/Pagination";
import BotaoExportacaoProgresso from "../../../components/BotaoExportacaoProgresso";
import useExportacaoAssincrona from "../Detalhe/hooks/useExportacaoAssincrona";
import ApuracaoCardShell from "../components/ApuracaoCardShell";
import SelectFiltro from "../components/SelectFiltro";
import { formatarCnpj, sumarizacaoEmAndamento } from "../utils";
import { formatarMoedaBRL } from "../Detalhe/utils/calcularTotaisPorAno.util";
import styles from "../Detalhe/detalhe.styles.module.css";
import tableStyles from "../../../components/TableCard/TableView/styles.module.css";
import {
  usePermissoes,
  CHAVES_APURACOES_EXPORT,
} from "../../../config/Context/PermissoesContext";

const NUM_TH = { textAlign: "right" };
const NUM_TD = { textAlign: "right", fontFamily: "ui-monospace, monospace" };

/**
 * TXM-599 — nomenclatura das colunas/KPIs segue o protótipo (apuracao_icms_detalhes.html,
 * aba ECF): "Lucro real" = base de cálculo do IRPJ, "IRPJ devido"/"CSLL devida" = valor
 * apurado no SPED, "Total devido" = irpjAPagar + csllAPagar. O protótipo calcula esses
 * valores com uma fórmula simplificada (sem compensação de prejuízo, PRONON/PRONAS,
 * retenções); aqui os valores vêm do SPED ECF real (blocos M/N), só o RÓTULO foi alinhado.
 *
 * IRPJ/CSLL podem ser negativos (crédito a compensar — a empresa pagou mais em estimativas
 * mensais do que o imposto anual apurado); é o valor real declarado no SPED, não recalculado.
 */
function calcularTotais(linhas) {
  return linhas.reduce(
    (acc, linha) => {
      const irpjDevido = Number(linha.irpjAPagar) || 0;
      const csllDevida = Number(linha.csllAPagar) || 0;
      acc.totalLucroReal += Number(linha.baseCalculoIrpj) || 0;
      acc.totalIrpjDevido += irpjDevido;
      acc.totalCsllDevida += csllDevida;
      acc.totalDevido += irpjDevido + csllDevida;
      return acc;
    },
    { totalLucroReal: 0, totalIrpjDevido: 0, totalCsllDevida: 0, totalDevido: 0 }
  );
}

export default function AbaEcf({ apuracaoId, onTotaisChange, bare }) {
  const navigate = useNavigate();
  const { pode } = usePermissoes();
  const podeExportar = pode(CHAVES_APURACOES_EXPORT);

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [cnpj, setCnpj] = useState("");
  const [infoSnack, setInfoSnack] = useState({ open: false, msg: "", severity: "info" });
  const [cnpjFiltro, setCnpjFiltro] = useState("");
  const [anoFiltro, setAnoFiltro] = useState("");
  const [linhasPagina, setLinhasPagina] = useState([]);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setLoading(true);
      setErro("");
      const res = await buscarSumarizacaoEcfApuracao(apuracaoId);
      if (!ativo) return;
      if (!res.sucesso) {
        setErro(res.mensagem || "Sumarização indisponível para esta apuração.");
        setLoading(false);
        return;
      }
      setLinhas(res.linhas);
      setCnpj(res.apuracao?.cnpj || "");
      setLoading(false);
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [apuracaoId]);

  /** TXM-418 — apuração ainda é de 1 CNPJ (multi-CNPJ é o TXM-601); filtro equaliza com o
   * protótipo, hoje com uma opção só. */
  const cnpjOpcoes = useMemo(
    () => [
      { value: "", label: "Todos" },
      ...(cnpj ? [{ value: cnpj, label: formatarCnpj(cnpj) }] : []),
    ],
    [cnpj]
  );

  const anoOpcoes = useMemo(() => {
    const anos = Array.from(new Set(linhas.map((linha) => linha.ano))).sort((a, b) => a - b);
    return [
      { value: "", label: "Todos" },
      ...anos.map((ano) => ({ value: String(ano), label: String(ano) })),
    ];
  }, [linhas]);

  const linhasFiltradas = useMemo(() => {
    let base = linhas;
    if (cnpjFiltro && cnpjFiltro !== cnpj) {
      base = [];
    }
    if (anoFiltro) {
      base = base.filter((linha) => String(linha.ano) === anoFiltro);
    }
    return [...base].sort((a, b) => a.ano - b.ano);
  }, [linhas, cnpj, cnpjFiltro, anoFiltro]);

  const totais = useMemo(() => calcularTotais(linhasFiltradas), [linhasFiltradas]);

  useEffect(() => {
    onTotaisChange?.(totais);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totais]);

  // Enfileira e acompanha o job até o download (antes só enfileirava e o arquivo nunca
  // chegava ao usuário) — ver Detalhe/hooks/useExportacaoAssincrona.js.
  const exportacao = useExportacaoAssincrona({
    apuracaoId,
    chave: "agrupado",
    enfileirar: () =>
      exportarSumarizacaoApuracao(apuracaoId, { tipo: "agrupado", formato: "csv" }),
    onFinalizar: ({ severity, msg }) => setInfoSnack({ open: true, msg, severity }),
  });

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <GridLoader color="#1565c0" size={12} />
      </Box>
    );
  }

  if (erro) {
    const emAndamento = sumarizacaoEmAndamento(erro);
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={emAndamento ? "info" : "error"}>
          {emAndamento
            ? "Sumarização em processamento — os dados aparecerão aqui assim que for concluída."
            : erro}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <ApuracaoCardShell
        bare={bare}
        conteudoComScrollProprio
        Icone={DescriptionIcon}
        titulo="Apuração do IRPJ/CSLL por ano-calendário"
        tituloSx={{ fontSize: "1.25rem" }}
        acaoHeader={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ color: "#666", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
              {linhasFiltradas.length} registros no filtro
            </Typography>
            <BotaoExportacaoProgresso
              progresso={exportacao}
              rotulo="Exportar CSV"
              variant="contained"
              size="small"
              disabled={!podeExportar}
              onClick={() => exportacao.iniciar()}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, bgcolor: "#18316e", minHeight: 40, whiteSpace: "nowrap", px: 2, "&:hover": { bgcolor: "#152858" } }}
            />
          </Box>
        }
        filtros={
          <>
            <SelectFiltro label="CNPJ" value={cnpjFiltro} onChange={setCnpjFiltro} options={cnpjOpcoes} />
            <SelectFiltro label="Ano-calendário" value={anoFiltro} onChange={setAnoFiltro} options={anoOpcoes} />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setCnpjFiltro("");
                setAnoFiltro("");
              }}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", minHeight: 40, color: "#18316e", borderColor: "#18316e", "&:hover": { borderColor: "#152858", bgcolor: "rgba(24, 49, 110, 0.04)" } }}
            >
              Limpar filtros
            </Button>
          </>
        }
        paginacao={
          linhasFiltradas.length > 0 && (
            <Pagination
              paginationKey="aba-ecf"
              data={linhasFiltradas}
              filteredData={linhasPagina}
              setFilteredData={setLinhasPagina}
            />
          )
        }
      >
        {linhasFiltradas.length === 0 ? (
          <p className={styles.emptyMsg}>Nenhum ano-calendário encontrado para esta apuração.</p>
        ) : (
          <div style={{ margin: "8px 0 0", overflow: "auto" }}>
          <table className={tableStyles.accordion_table}>
            <thead>
              <tr>
                <th scope="col" className={`${tableStyles.stickyKeyHeader} ${tableStyles.stickyKeyCol1}`}>CNPJ</th>
                <th scope="col" className={`${tableStyles.stickyKeyHeader} ${tableStyles.stickyKeyCol2}`}>Ano-calendário</th>
                <th scope="col" style={NUM_TH}>Lucro líquido</th>
                <th scope="col" style={NUM_TH}>Adições</th>
                <th scope="col" style={NUM_TH}>Exclusões</th>
                <th scope="col" style={NUM_TH}>Lucro real</th>
                <th scope="col" style={NUM_TH}>IRPJ devido</th>
                <th scope="col" style={NUM_TH}>Base CSLL</th>
                <th scope="col" style={NUM_TH}>CSLL devida</th>
                <th scope="col" style={NUM_TH}>Total devido</th>
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhasPagina.map((linha) => {
                const irpjDevido = Number(linha.irpjAPagar) || 0;
                const csllDevida = Number(linha.csllAPagar) || 0;
                return (
                  <tr key={linha.ano}>
                    <td className={`${tableStyles.stickyKeyCell} ${tableStyles.stickyKeyCol1}`}>{formatarCnpj(cnpj)}</td>
                    <td className={`${tableStyles.stickyKeyCell} ${tableStyles.stickyKeyCol2}`}>{linha.ano}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.lucroLiquidoAntesIrpj)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.totalAdicoes)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.totalExclusoes)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.baseCalculoIrpj)}</td>
                    <td style={{ ...NUM_TD, color: "#c62828" }}>{formatarMoedaBRL(irpjDevido)}</td>
                    <td style={NUM_TD}>{formatarMoedaBRL(linha.baseCalculoCsll)}</td>
                    <td style={{ ...NUM_TD, color: "#c62828" }}>{formatarMoedaBRL(csllDevida)}</td>
                    <td style={{ ...NUM_TD, color: "#c62828" }}>{formatarMoedaBRL(irpjDevido + csllDevida)}</td>
                    <td>
                      <button
                        type="button"
                        className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                        onClick={() =>
                          navigate(
                            `/relatorios/apuracoes/${apuracaoId}/detalhe/icms/lancamentos-ecf?ano=${linha.ano}`
                          )
                        }
                      >
                        Detalhes
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </ApuracaoCardShell>

      <Snackbar
        open={infoSnack.open}
        autoHideDuration={4000}
        onClose={() => setInfoSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={infoSnack.severity}>{infoSnack.msg}</Alert>
      </Snackbar>
    </>
  );
}
